import { useWebSocket as useAhooksWebSocket } from 'ahooks'
import { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import type { WindowType } from '@/vite-env'
import EventBus, { IEventBus } from '@/utils/eventBus'

/**
  enum ReadyState {
    Connecting = 0,
    Open = 1,
    Closing = 2,
    Closed = 3,
  }
 */

// 获取当前路径
const getRoutePath = (path: string) => {
  if (path.endsWith('/home') || path === '/') {
    return 'HOME'
  } else {
    return path
  }
}

const useWebSocket = (url: string) => {
  // 是否手动断开连接
  const isManualDisconnect = useRef(false)
  // 格式化后的消息
  const formattedMessage = useRef<any>(null)
  // 当前路径
  const location = useLocation()
  // 是否可以连接
  const [shouldConnect, setShouldConnect] = useState(false)

  // 检查 Global_Front_Api 是否可用

  const { readyState, latestMessage, disconnect, connect } = useAhooksWebSocket(
    shouldConnect
      ? `${window?.Global_Front_Api?.front_gea_api___gea_ws_api}${url}`
      : `${import.meta.env.VITE_WEBSOCKET_URL}${url}`,
    {
      manual: true,
      reconnectLimit: 10,
      reconnectInterval: 3000,
      onOpen: () => {
        console.log('websocket success')
      },
      onClose: () => {
        // 如果不是手动断开连接，则重新连接websocket
        if (!(window as unknown as WindowType).isManualDisconnect) {
          // 如果语言不一致，则不重新连接websocket，说明是切换语言了
          if (localStorage.getItem('language') !== (window as unknown as WindowType).lastLanguage) {
            // 语言切换后记录当前语言
            ;(window as unknown as WindowType).lastLanguage =
              localStorage.getItem('language') || 'en_US'
            return
          }
          // 地址不一致，则不重新连接websocket，说明是切换地址了
          const lastPath = getRoutePath(localStorage.getItem('currentPath') || '')
          const currentPath = getRoutePath(location.pathname)
          if (lastPath !== currentPath) {
            // 地址切换后记录当前地址
            ;(window as unknown as WindowType).lastPath = location.pathname
            return
          }
          const timeId = setTimeout(() => {
            connect?.()
            clearTimeout(timeId)
          }, 1000)
        }
      },
      onError: (error) => {
        console.log('websocket error', error)
        // 当websocket连接错误时，如果手动断开连接为false，则重新连接websocket
        // if (!(window as unknown as WindowType).isManualDisconnect) {
        //   connect?.()
        // }
      },
    },
  )
  // let formattedMessage: any = null
  const isReady = readyState === 1 && latestMessage !== undefined
  if (isReady) {
    try {
      if (typeof latestMessage.data === 'string') {
        formattedMessage.current = JSON.parse(latestMessage.data)
      }
    } catch (error) {
      console.error('Failed to parse WebSocket message:', error)
    }
  }

  // 离开时间
  const leaveTime = useRef<number>(0)
  useEffect(() => {
    // 只有在['/home']页面，监听用户鼠标2分钟，如果没有行为，则断开websocket连接，并弹窗提醒用户重新链接
    if (!['/home'].some((item) => location.pathname.endsWith(item))) return
    // 事件触发的时间戳
    let timeoutId: NodeJS.Timeout | null = null
    function showReconnectDialog() {
      EventBus.emit(IEventBus.WebSocketConnectShow, true)
    }
    // 检测无动作
    const resetTimer = () => {
      leaveTime.current = Date.now()
      if (timeoutId) clearTimeout(timeoutId)
      timeoutId = setTimeout(
        () => {
          // 设置手动断开连接为true
          // 断开websocket连接
          // 弹窗提醒用户重新链接
          ;(window as unknown as WindowType).isManualDisconnect = true
          isManualDisconnect.current = true
          disconnect?.()
          showReconnectDialog()
          console.log('11')
        },
        2 * 60 * 1000,
      ) // 2分钟
    }
    // 需要注册的用户交互事件
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'touchmove']
    events.forEach((event) => window.addEventListener(event, resetTimer))
    resetTimer()
    // 清理事件监听和定时器
    return () => {
      events.forEach((event) => window.removeEventListener(event, resetTimer))
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [disconnect])

  useEffect(() => {
    ;(window as unknown as WindowType).isManualDisconnect = false
    const checkGlobalApi = () => {
      const api = window?.Global_Front_Api
      if (api && api.front_gea_api___gea_ws_api) {
        setShouldConnect(true)
      } else {
        // 如果还没有，继续等待
        setTimeout(checkGlobalApi, 100)
      }
    }
    checkGlobalApi()
    connect()
    return () => {
      // 卸载等于手动断开连接
      ;(window as unknown as WindowType).isManualDisconnect = true
    }
  }, [])
  return { isReady, formattedMessage: formattedMessage.current }
}

export default useWebSocket
